import { GrayDark, White } from "@traceair/tds";
import React, { Component } from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { style } from "typestyle";
import AclCondition from "../../../acl/containers/AclCondition";
import { AclState } from "../../../acl/reducer";
import { siteHasPermissions } from "../../../acl/utils";
import {
  CurrentSiteState,
  getCurrentProj4,
} from "../../../redux/modules/currentSite/reducer";
import {
  getCurrentSiteState,
  GlobalState,
} from "../../../redux/modules/rootReducer";
import { pushRoute } from "../../../redux/modules/route/actions";

import {
  generate,
  ROUTE_SITE_WORKFLOW,
  ROUTE_SITE_WORKFLOW_CREATE,
} from "../../../redux/modules/route/routes";
import {
  createCompositeSiteId,
  getSiteById,
  Site,
} from "../../../redux/modules/sites/reducer";
import { WORKFLOW_TYPE_OVERLAYS } from "../../../redux/modules/workflow/constants";
import {
  Workflow,
  WorkflowsState,
} from "../../../redux/modules/workflow/reducer";
import {
  getWorkflowActionPermission,
  getWorkflowInitTitle,
  getWorkflowLogo,
  getWorkflowStartPermission,
  getWorkflowTitle,
  getWorkflowViewPermission,
} from "../../../redux/modules/workflow/strings";
import { User } from "../../../typings";
import Button from "../../components/Button";
import CopyToClipboardButton from "../../components/CopyToClipboardButton";
import Jumbotron from "../../components/Jumbotron";
import PageLayout from "../../components/PageLayout";
import PlusCircleIcon from "../../icons/PlusCircle";
import NavBar from "../../NavBar/NavBar";
import AlertError from "../workflowView/AlertError";
import { userOpensWorkflowListPage } from "./scenarios";
import WorkflowsList from "./WorkflowList";

// It's a bit odd that this list wasn't defined before
// anywhere in the app
const isWorkflowSupported = (workflowType: string) => {
  const type = workflowType.toLowerCase();
  return (
    type === "overlays" ||
    type === "target_surface" ||
    type === "upload_dem" ||
    type === "base_surface" ||
    type === "preprocessed_data" ||
    type === "gcp"
  );
};

type IOwnProps = {
  customerId: string;
  siteId: string;
  workflowType: string;
};

type WorkflowListPageProps = IOwnProps & {
  // injected by connect
  acl: AclState;
  siteState: CurrentSiteState;
  site: Site;
  user: User;
  workflowsState: WorkflowsState;

  // bound actions
  pushRoute: (url: string) => AnyAction;
  userOpensWorkflowListPage: (
    customerId: string,
    siteId: string,
    workflowType: string,
  ) => Promise<void>;
};

class WorkflowListPage extends Component<WorkflowListPageProps> {
  componentDidMount() {
    const { customerId, siteId, workflowType, userOpensWorkflowListPage } =
      this.props;
    userOpensWorkflowListPage(customerId, siteId, workflowType);
  }

  doInitWorkflow(workflowType: string) {
    const { pushRoute, customerId, siteId } = this.props;

    pushRoute(
      generate(ROUTE_SITE_WORKFLOW_CREATE, {
        customerId,
        siteId,
        workflowType: workflowType.toLowerCase(),
      }),
    );
  }

  render() {
    return (
      <PageLayout
        navBar={<NavBar />}
        header={this.renderHeader()}
        content={this.renderContent()}
      />
    );
  }

  renderHeader() {
    const { workflowType } = this.props;
    return (
      <Jumbotron
        header={getWorkflowTitle(workflowType) || "Not found"}
        imageUrl={getWorkflowLogo(workflowType.toUpperCase())}
      />
    );
  }

  renderContent() {
    if (!this.props.site) {
      return null;
    }

    if (!isWorkflowSupported(this.props.workflowType)) {
      return (
        <AlertError
          header="Not found"
          message="Workflow not found or no longer available"
        />
      );
    }

    return (
      // TODO: test react children rendering feature after updating to 16.x.x
      <div>
        {this.renderNoSrsAlert()}

        <div
          className={style({
            display: "flex",
            columnGap: 16,
          })}
        >
          {this.props.workflowType === WORKFLOW_TYPE_OVERLAYS &&
            this.renderCopyProj4Button()}
          {this.renderInitWorkflowButton()}
        </div>
        {this.renderWorkflowsList()}
      </div>
    );
  }

  renderCopyProj4Button() {
    const { siteState } = this.props;
    const proj4 = getCurrentProj4(siteState);

    if (!proj4) {
      return null;
    }

    return (
      <div
        className={style({
          flex: "1 1 50%",
        })}
      >
        <CopyToClipboardButton
          textToCopy={proj4}
          label={"Copy PROJ.4 string"}
        />
      </div>
    );
  }

  renderNoSrsAlert() {
    const { siteState } = this.props;
    const { pending } = siteState;
    const proj4 = getCurrentProj4(siteState);

    if (pending || proj4) {
      return null;
    }

    return (
      <AlertError
        header="No SRS"
        message="Please, complete the site registration and provide a Spatial Reference System."
      />
    );
  }

  renderInitWorkflowButton() {
    const { workflowType, site } = this.props;
    const workflowTypeUpper = workflowType.toUpperCase();

    return (
      <AclCondition
        site={site}
        permissions={[
          getWorkflowViewPermission(workflowType),
          getWorkflowStartPermission(workflowType),
        ]}
        ifTrue={renderButton.call(this, false)}
        ifFalse={renderButton.call(this, true)}
      />
    );

    function renderButton(disabled: boolean) {
      return (
        <div
          className={style({
            flex: "1 1 50%",
          })}
        >
          <Button
            icon={
              <PlusCircleIcon
                color={disabled ? GrayDark : White}
                size={"32px"}
              />
            }
            label={getWorkflowInitTitle(workflowTypeUpper)}
            size={"big"}
            type={"primary"}
            disabled={disabled}
            className={`autotest-init-workflow-button_${
              disabled ? "disabled" : "enabled"
            }`}
            onClick={() =>
              disabled ? () => null : this.doInitWorkflow(workflowTypeUpper)
            }
          />
        </div>
      );
    }
  }

  renderWorkflowsList() {
    const { customerId, siteId, site, workflowType, pushRoute, acl } =
      this.props;
    return (
      <AclCondition
        site={site}
        permissions={[getWorkflowViewPermission(workflowType)]}
      >
        <WorkflowsList
          isModerator={siteHasPermissions(
            acl,
            [getWorkflowActionPermission(workflowType)],
            createCompositeSiteId(site),
          )}
          workflowsState={this.props.workflowsState}
          onWorkflowClick={({ id }: Workflow) => {
            pushRoute(
              generate(ROUTE_SITE_WORKFLOW, {
                customerId,
                siteId,
                workflowType: workflowType.toLowerCase(),
                workflowId: id,
              }),
            );
          }}
        />
      </AclCondition>
    );
  }
}

export default connect(
  (state: GlobalState, ownProps: IOwnProps) => ({
    ...ownProps,
    acl: state.acl,
    siteState: getCurrentSiteState(state),
    site: getSiteById(state.sites, ownProps.customerId, ownProps.siteId),
    user: state.user,
    workflowsState: state.workflows,
  }),
  {
    pushRoute,
    userOpensWorkflowListPage,
  },
)(WorkflowListPage);
